import { P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { QuoteParcels } from '~/lib/types/basket-types';
import useBasketSection from '~/hooks/use-basket-section';
import { FC, Suspense, lazy, useMemo, useState } from 'react';
import Parcels from '~/components/parcel-details/parcel/parcels';
import useQuote from '~/hooks/use-quote';
import { BasketSections, RequirementTypes } from '~/lib/types/enums';
import useQuoteRequirements from '~/hooks/use-quote-requirements';
import { DimensionsWarning } from '~/components/parcel-details/dimensions/dimension-warning';
import { BaseModuleProps } from './module-factory';
import Section from '~/components/core/section/section';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

const CmsContent = lazy(() => import('~/components/core/content/cms-content'));

const ParcelsModule: FC<BaseModuleProps & { initialSummary: boolean }> = ({ errors, wrapper, initialSummary }) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { quote } = useQuote();
  const { requirements } = useQuoteRequirements();
  const requirement = useMemo(() => requirements[RequirementTypes.PARCELS] ?? {}, [requirements]);
  const section = useBasketSection<QuoteParcels>({ name: BasketSections.PARCELS });
  const [summaryView, setSummaryView] = useState(
    initialSummary && !section.data.parcels.some((p) => !p.value || !p.contentSummary || !p.weight)
  );

  if (summaryView) {
    return wrapper(
      <Section
        title={t('PARCEL_SUMMARY_TITLE', { defaultValue: 'Parcel Summary' })}
        dataTestId={`parcels-section-summary`}
        loading={section.loading || !quote}
      >
        <P2GGrid container spacing={2} direction="column">
          {section.data.parcels.map((p) => {
            const allParcelDimsDefined = p.length && p.width && p.height;
            return (
              <P2GGrid item key={p.id}>
                <P2GText component="p" variant="subtitle1" fontWeight="bold" color="inherit">
                  {p.contentSummary}
                </P2GText>
                <P2GText component="p" mt={1} display="flex" gap={1}>
                  <P2GText>{p.weight}kg</P2GText>
                  {allParcelDimsDefined && (
                    <>
                      <span>|</span>
                      {p.length}cm x {p.width}cm x {p.height}cm
                    </>
                  )}
                </P2GText>
              </P2GGrid>
            );
          })}
          <P2GGrid item>
            <P2GLink onClick={() => setSummaryView(false)}>Edit Details</P2GLink>
          </P2GGrid>
        </P2GGrid>
      </Section>
    );
  }

  return wrapper(
    <P2GGrid container spacing={2} direction="column" dataTestId="parcels-section">
      <P2GGrid item>
        <Parcels section={section} quote={quote} errors={errors} requirements={requirement} currency={quote.currency} />
      </P2GGrid>
      <Suspense>
        <CmsContent
          contentKey="packaging-advice"
          wrapper={(x) => (
            <P2GGrid item xs={12}>
              {x}
            </P2GGrid>
          )}
        />
      </Suspense>
      <DimensionsWarning restrictions={quote.restrictions} />
    </P2GGrid>
  );
};

export default ParcelsModule;
